import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { LogIn, KeyRound, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useBranding } from '../context/BrandingContext';
import { publicBackgroundImageUrl } from '../modules/branding/api/brandingApi';
import { navigate } from '../shared/lib/router';
import { apiClient, ApiError, configureApiClient } from '../shared/lib/apiClient';
import { useTurnstile } from '../shared/lib/useTurnstile';

// With no site key configured (every environment's default today, local dev
// included), useTurnstile is a complete no-op and this page behaves exactly
// as before -- see useTurnstile.js.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// Maps backend error codes to a specific message key. Deliberately generic
// for INVALID_CREDENTIALS/ACCOUNT_LOCKED-adjacent auth failures (standard
// practice: don't help an attacker distinguish "wrong password" from
// "unknown email"), but specific for everything else -- a wrong tenant slug
// or a network hiccup isn't a credential-enumeration risk, and silently
// giving both the same message just makes the form impossible to debug.
const ERROR_CODE_TO_MESSAGE_KEY = {
  TENANT_NOT_FOUND: 'login.errorTenantNotFound',
  TENANT_REQUIRED: 'login.errorTenantNotFound',
  TENANT_SUSPENDED: 'login.errorTenantSuspended',
  ACCOUNT_LOCKED: 'login.errorAccountLocked',
  RATE_LIMITED: 'login.errorRateLimited',
  NETWORK_ERROR: 'login.errorNetwork',
};

function loginErrorMessageKey(err) {
  return ERROR_CODE_TO_MESSAGE_KEY[err?.code] || 'login.errorInvalidCredentials';
}

const MFA_ERROR_CODE_TO_MESSAGE_KEY = {
  INVALID_CODE: 'mfa.errorInvalidCode',
  INVALID_OR_EXPIRED_CHALLENGE: 'mfa.errorExpired',
  METHOD_NOT_ENABLED: 'mfa.errorMethodNotEnabled',
  RATE_LIMITED: 'login.errorRateLimited',
  NETWORK_ERROR: 'login.errorNetwork',
  WEBAUTHN_VERIFICATION_FAILED: 'mfa.errorWebauthnFailed',
};

// Unlike loginErrorMessageKey's fallback (deliberately generic for
// credential-enumeration reasons on the password step), there's no such
// concern once a user is already past the password check -- so an unmapped
// code here should show an honestly generic "something went wrong" instead
// of *specifically* claiming the code was wrong, which is actively
// misleading for errors that have nothing to do with the code at all (e.g.
// a tenant-resolution problem masquerading as "That code is incorrect" for
// this exact reason cost real debugging time before this fix).
function mfaErrorMessageKey(err) {
  return MFA_ERROR_CODE_TO_MESSAGE_KEY[err?.code] || 'errorBanner';
}

// Shared by the password-only login path and every MFA-completion path: a
// password older than PASSWORD_EXPIRY_DAYS blocks a session exactly like an
// unverified second factor does, returning { passwordChangeRequired,
// resetToken } instead of tokens. Reuses the same
// /password-reset/confirm?token=... page as "forgot password" and "accept
// your invite" (see PasswordResetConfirmPage's isExpired variant) since
// completing it is the identical action.
// Shared by both auth-screen render paths below (the password/tenant form
// and the MFA challenge step) so the background/overlay stay visually
// consistent across the two-step login flow instead of flashing away and
// back on the MFA step.
function authScreenBackgroundStyle(branding, tenantSlug) {
  if (!branding.backgroundImageId) return {};
  return { backgroundImage: `url(${publicBackgroundImageUrl(tenantSlug, branding.backgroundImageId)})` };
}

function AuthScreenOverlay({ branding }) {
  if (!branding.backgroundImageId || !branding.overlayColor) return null;
  return <div className="auth-screen__overlay" style={{ backgroundColor: branding.overlayColor, opacity: branding.overlayOpacity ?? 0 }} />;
}

function redirectIfPasswordChangeRequired(result, tenantSlug) {
  if (!result?.passwordChangeRequired) return false;
  // tenantSlug is also included here (not just relied on via
  // configureApiClient's earlier call) so the confirm page resolves the
  // right tenant even on a full page reload, not just within this same
  // client-side session -- same reasoning as the invite/forgot-password
  // links in administration/routes.js and core/routes.js.
  const tenantParam = tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : '';
  navigate(`/password-reset/confirm?token=${encodeURIComponent(result.resetToken)}&reason=expired${tenantParam}`);
  return true;
}

/**
 * Second login step, shown when POST /auth/login responds with
 * `{ mfaRequired: true, mfaToken, availableMethods, hasBackupCodes }`
 * instead of a session. Submitting the right endpoint for the chosen
 * method (see AuthContext.completeMfaChallenge) applies a session exactly
 * like a non-MFA login.
 */
function MfaChallengeStep({ challenge, onBack, tenantSlug, branding }) {
  const { t } = useI18n();
  const { completeMfaChallenge } = useAuth();

  const initialMode = challenge.availableMethods.includes('totp')
    ? 'totp'
    : challenge.availableMethods.includes('webauthn')
      ? 'webauthn'
      : 'backup_code';
  const [mode, setMode] = useState(initialMode);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitCode = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const path = mode === 'backup_code' ? '/auth/mfa/login/backup-code' : '/auth/mfa/login/totp';
    try {
      const result = await completeMfaChallenge(path, { mfaToken: challenge.mfaToken, code });
      redirectIfPasswordChangeRequired(result, tenantSlug);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const useSecurityKey = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const optionsRes = await apiClient.post('/auth/mfa/login/webauthn-options', { mfaToken: challenge.mfaToken });
      const response = await startAuthentication({ optionsJSON: optionsRes.data });
      const result = await completeMfaChallenge('/auth/mfa/login/webauthn-verify', { mfaToken: challenge.mfaToken, response });
      redirectIfPasswordChangeRequired(result, tenantSlug);
    } catch (err) {
      // startAuthentication() throws plain DOMExceptions (e.g. the user
      // cancelled the browser prompt), which don't have an ApiError `code`
      // -- mfaErrorMessageKey's fallback covers that case too.
      setError(err instanceof ApiError ? err : { code: 'WEBAUTHN_VERIFICATION_FAILED' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen" style={authScreenBackgroundStyle(branding, tenantSlug)}>
      <AuthScreenOverlay branding={branding} />
      <div className="auth-card">
        <h1 className="auth-card__title">
          <ShieldCheck size={22} aria-hidden="true" /> {t('common', 'mfa.title')}
        </h1>
        <p className="auth-card__subtitle">{t('common', `mfa.subtitle.${mode}`)}</p>

        {error && <div className="error-banner">{t('common', mfaErrorMessageKey(error))}</div>}

        {mode === 'webauthn' ? (
          <button type="button" className="btn btn-primary btn-block" onClick={useSecurityKey} disabled={submitting}>
            <KeyRound size={16} /> {t('common', 'mfa.useSecurityKeyButton')}
          </button>
        ) : (
          <form onSubmit={submitCode}>
            <div className="form-field">
              <label className="form-label" htmlFor="mfa-code">
                {t('common', mode === 'backup_code' ? 'mfa.backupCodeLabel' : 'mfa.codeLabel')}
              </label>
              <input
                id="mfa-code"
                className="form-input"
                type="text"
                inputMode={mode === 'backup_code' ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                placeholder={t('common', mode === 'backup_code' ? 'mfa.backupCodePlaceholder' : 'mfa.codePlaceholder')}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {t('common', 'mfa.verifyButton')}
            </button>
          </form>
        )}

        <div className="auth-card__mfa-alternatives">
          {mode !== 'totp' && challenge.availableMethods.includes('totp') && (
            <button type="button" className="btn-link" onClick={() => { setMode('totp'); setError(null); setCode(''); }}>
              {t('common', 'mfa.useAuthenticatorAppInstead')}
            </button>
          )}
          {mode !== 'webauthn' && challenge.availableMethods.includes('webauthn') && (
            <button type="button" className="btn-link" onClick={() => { setMode('webauthn'); setError(null); setCode(''); }}>
              {t('common', 'mfa.useSecurityKeyInstead')}
            </button>
          )}
          {mode !== 'backup_code' && challenge.hasBackupCodes && (
            <button type="button" className="btn-link" onClick={() => { setMode('backup_code'); setError(null); setCode(''); }}>
              {t('common', 'mfa.useBackupCodeInstead')}
            </button>
          )}
        </div>

        <button type="button" className="btn-link" onClick={onBack}>
          <ArrowLeft size={14} /> {t('common', 'mfa.backToLogin')}
        </button>
      </div>
    </div>
  );
}

export default function LoginPage() {
  useNamespace('common');
  const { t } = useI18n();
  const { login } = useAuth();
  const { tenantSlug: defaultTenantSlug } = useTenant();

  const branding = useBranding();

  // Prefer a "?tenant=" query param read directly off the URL at mount time
  // over TenantContext's own guess: that context resolves its value ONCE
  // at the whole app's initial load and never re-derives it afterward (by
  // design -- see its own comment on why it can't double as the
  // authenticated tenant resolver), so it wouldn't reflect a query param
  // added by a client-side redirect (e.g. logout preserving the just-used
  // tenant) without a full page reload. LoginPage itself, on the other
  // hand, genuinely unmounts and remounts fresh across a login/logout
  // cycle, so re-reading the URL directly here picks that up immediately.
  const [tenantSlug, setTenantSlug] = useState(
    () => new URLSearchParams(window.location.search).get('tenant') || defaultTenantSlug || ''
  );
  // Set by AuthContext's auto-logout timer (see its own "reason: 'expired'"
  // comment) redirecting here with "?reason=expired" -- read once at mount,
  // same as the tenant slug above, since this page genuinely remounts fresh
  // across a login/logout cycle.
  const [sessionExpired] = useState(() => new URLSearchParams(window.location.search).get('reason') === 'expired');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const turnstileRef = useTurnstile(TURNSTILE_SITE_KEY, setTurnstileToken);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // POST /auth/login carries this tenantSlug in its own body (see
      // AuthContext.login), so it always resolves correctly regardless of
      // what TenantContext's pre-login URL-based guess was. But the
      // follow-up /auth/mfa/login/* calls (if a second factor is required)
      // have no such body field -- they rely entirely on the X-Tenant-Slug
      // header, which without this line would still be that stale pre-login
      // guess. Locking it in here, the moment the user submits a tenant
      // slug, ensures every subsequent request in this login attempt
      // targets the right tenant. (AuthContext.applySession does the same
      // thing again once a full session exists; this covers the gap before
      // that point.)
      configureApiClient({ getTenantSlug: () => tenantSlug });

      const result = await login(email, password, tenantSlug, turnstileToken);
      if (result.mfaRequired) {
        setMfaChallenge(result);
      } else {
        redirectIfPasswordChangeRequired(result, tenantSlug);
      }
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (mfaChallenge) {
    return <MfaChallengeStep challenge={mfaChallenge} tenantSlug={tenantSlug} branding={branding} onBack={() => setMfaChallenge(null)} />;
  }

  return (
    <div className="auth-screen" style={authScreenBackgroundStyle(branding, tenantSlug)}>
      <AuthScreenOverlay branding={branding} />
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        <h1 className="auth-card__title">{t('common', 'appName')}</h1>
        <p className="auth-card__subtitle">{t('common', 'login.title')}</p>
        {branding.welcomeMessage && <p className="auth-card__welcome-message">{branding.welcomeMessage}</p>}
        {sessionExpired && !error && <p className="auth-card__hint">{t('common', 'login.sessionExpiredNotice')}</p>}

        {error && <div className="error-banner">{t('common', loginErrorMessageKey(error))}</div>}

        <div className="form-field">
          <label className="form-label" htmlFor="login-tenant">
            {t('common', 'login.tenantLabel')}
          </label>
          <input
            id="login-tenant"
            className="form-input"
            type="text"
            value={tenantSlug}
            placeholder={t('common', 'login.tenantPlaceholder')}
            onChange={(event) => setTenantSlug(event.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="login-email">
            {t('common', 'login.emailLabel')}
          </label>
          <input
            id="login-email"
            className="form-input"
            type="email"
            value={email}
            placeholder={t('common', 'login.emailPlaceholder')}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="username"
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="login-password">
            {t('common', 'login.passwordLabel')}
          </label>
          <input
            id="login-password"
            className="form-input"
            type="password"
            value={password}
            placeholder={t('common', 'login.passwordPlaceholder')}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
          />
        </div>

        <div ref={turnstileRef} id="turnstile-widget" className="turnstile-widget" />
        <p className="auth-card__hint">{t('common', 'login.turnstileNotice')}</p>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={submitting || (TURNSTILE_SITE_KEY && !turnstileToken)}
        >
          <LogIn size={16} /> {t('common', 'login.submit')}
        </button>

        <button
          type="button"
          className="btn-link"
          onClick={() => navigate('/password-reset')}
        >
          {t('common', 'login.forgotPassword')}
        </button>
      </form>
    </div>
  );
}
